import { createContext, use } from "react";
import type { AppView, AuthUser, LoginRequest, Role } from "@/contracts";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used inside <AuthProvider>.");
  }

  return value;
}

/**
 * Role check used by route guards and by the navigation.
 *
 * A super admin is not treated as a wildcard here: a screen that lists one
 * organization's data still has to name SUPER_ADMIN explicitly, so widening
 * access is always a visible edit rather than a side effect.
 */
export function hasRole(user: AuthUser | null, allowed: readonly Role[]): boolean {
  return user !== null && allowed.includes(user.role);
}

/**
 * Membership view check (ADR-088). SUPER_ADMIN is treated as holding every
 * view because the platform session has no membership allowlist.
 */
export function hasView(user: AuthUser | null, view: AppView | undefined): boolean {
  if (user === null) {
    return false;
  }

  if (view === undefined || user.role === "SUPER_ADMIN") {
    return true;
  }

  return user.views.includes(view);
}

/** True when the membership holds at least one of the named views. */
export function hasAnyView(user: AuthUser | null, views: readonly AppView[]): boolean {
  return views.some((view) => hasView(user, view));
}
